import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Check } from 'lucide-react'
import { departamentulRolului, numeDepartament } from '@b4l/shared'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Modal } from '../../components/ui/Modal'
import { Button } from '../../components/ui/Button'
import { Avatar } from './CardFata'
import { numeOameni, useResponsabili, type Persoana } from './date'

interface Om {
  id: number
  name: string
  role: string
}

/** Colegii activi — aceeasi lista ca la @mentiuni. Tinuta minte 10 minute. */
export function useOameni() {
  return useQuery<{ users: Om[] }>({
    queryKey: ['taskuri', 'oameni'],
    queryFn: async () => (await api.get('/tasks/mentionable-users')).data,
    staleTime: 10 * 60_000,
  })
}

/**
 * Alegerea oamenilor unui task, cu bife (19.10.2026: „oricine atribuie oricui", unul sau mai
 * multi). Aceeasi peste tot: „Task nou", panoul task-ului, „Trimite mai departe".
 *
 * Tu esti primul („Eu"), apoi oamenii departamentului ales — de cele mai multe ori acolo e omul
 * potrivit —, apoi restul, alfabetic.
 */
export function ListaOameni({
  alesi,
  setAlesi,
  departament = null,
  eticheta = 'Oamenii',
  inaltime = 'max-h-56',
  blocati = [],
}: {
  alesi: number[]
  setAlesi: (ids: number[]) => void
  departament?: string | null
  eticheta?: string
  inaltime?: string
  /** Bifati care nu se pot debifa: pe altii de pe task ii scoate doar adminul (sau cine l-a dat). */
  blocati?: number[]
}) {
  const eu = useAuthStore((s) => s.user?.id)
  const oameni = useOameni()
  const [cauta, setCauta] = useState('')

  const lista = useMemo(() => {
    const q = cauta.trim().toLowerCase()
    return (oameni.data?.users ?? [])
      .filter((u) => !q || u.name.toLowerCase().includes(q))
      .map((u) => ({ ...u, dep: departamentulRolului(u.role) }))
      .sort(
        (a, b) =>
          Number(b.id === eu) - Number(a.id === eu) ||
          Number(b.dep === departament) - Number(a.dep === departament) ||
          a.name.localeCompare(b.name, 'ro'),
      )
  }, [oameni.data, cauta, departament, eu])

  const comuta = (id: number) =>
    setAlesi(alesi.includes(id) ? alesi.filter((x) => x !== id) : [...alesi, id])

  return (
    <div className="grid gap-2">
      <input
        value={cauta}
        onChange={(e) => setCauta(e.target.value)}
        placeholder="Caută după nume…"
        aria-label={`${eticheta}: caută un coleg`}
        className="w-full rounded-caseta border border-b4l-border bg-b4l-surface px-2.5 py-1.5 text-corp text-b4l-text"
      />
      {oameni.isLoading ? (
        <p className="text-mic text-b4l-text-dim">Se încarcă…</p>
      ) : (
        <ul
          className={`grid ${inaltime} gap-0.5 overflow-y-auto rounded-caseta border border-b4l-border p-1`}
          role="group"
          aria-label={`${eticheta}, poți alege mai mulți`}
        >
          {lista.map((u) => {
            const ales = alesi.includes(u.id)
            return (
              <li key={u.id}>
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={ales}
                  aria-disabled={ales && blocati.includes(u.id)}
                  title={
                    ales && blocati.includes(u.id)
                      ? 'Ceilalți colegi pot fi scoși de pe task doar de un admin (la un task privat, de cine l-a creat).'
                      : undefined
                  }
                  onClick={() => !(ales && blocati.includes(u.id)) && comuta(u.id)}
                  className={`flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-b4l-blue ${
                    ales ? 'bg-b4l-blue-light' : 'hover:bg-b4l-bg'
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`grid h-4 w-4 shrink-0 place-items-center rounded-[4px] border ${
                      ales ? 'border-b4l-blue bg-b4l-blue text-white' : 'border-b4l-text-dim'
                    }`}
                  >
                    {ales && <Check size={11} strokeWidth={3} />}
                  </span>
                  <Avatar nume={u.name} mic={false} />
                  <span className="grid min-w-0 flex-1">
                    <span className="truncate text-corp text-b4l-text">
                      {u.name}
                      {u.id === eu && <span className="text-b4l-text-dim"> · eu</span>}
                    </span>
                    <span className="text-mic text-b4l-text-dim">
                      {u.dep ? numeDepartament(u.dep) : 'admin'}
                    </span>
                  </span>
                </button>
              </li>
            )
          })}
          {lista.length === 0 && (
            <li className="px-2 py-3 text-mic text-b4l-text-muted">Niciun coleg cu numele ăsta.</li>
          )}
        </ul>
      )}
    </div>
  )
}

/**
 * Oamenii unui task existent: pui si scoti, apoi „Salvează". Serverul hotaraste cine are voie
 * (`verificaResponsabilii`) si ce se intampla cand nu mai ramane nimeni — public: „De preluat";
 * privat: inapoi la cine l-a dat.
 */
export function AlegeOameni({
  task,
  laInchidere,
}: {
  task: {
    id: number
    titlu: string
    departament: string | null
    privat: boolean
    responsabili: Persoana[]
    /** Poate scoate pe altii (serverul decide; aici doar blocheaza bifele). */
    poateScoateAltii: boolean
  }
  laInchidere: () => void
}) {
  const eu = useAuthStore((s) => s.user?.id)
  const initiali = task.responsabili.map((r) => r.id)
  const [alesi, setAlesi] = useState<number[]>(initiali)
  const schimba = useResponsabili()
  const oameni = useOameni()
  const adauga = alesi.filter((id) => !initiali.includes(id))
  const scoate = initiali.filter((id) => !alesi.includes(id))
  const nume = (ids: number[]) =>
    numeOameni(ids.map((id) => ({ name: oameni.data?.users.find((u) => u.id === id)?.name ?? '' })))

  return (
    <Modal open onClose={laInchidere} title="Cine lucrează la task?">
      <div className="grid gap-3">
        <p className="text-mic text-b4l-text-muted">
          „{task.titlu}”.{' '}
          {task.privat
            ? 'E privat: îl văd doar cei bifați și cine l-a dat.'
            : 'Cei bifați îl au „În lucru”; fără nimeni, se întoarce la „De preluat”.'}{' '}
          Oricare dintre ei îl poate muta și închide. Cine e pus primește o notificare și un email.
        </p>
        <ListaOameni
          alesi={alesi}
          setAlesi={setAlesi}
          departament={task.departament}
          inaltime="max-h-[45vh]"
          // Te poti scoate pe tine; pe altii doar daca ai voie (adminul; la privat, cine l-a dat).
          blocati={task.poateScoateAltii ? [] : initiali.filter((id) => id !== eu)}
        />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-mic text-b4l-text-dim">
            {adauga.length > 0 && `Intră: ${nume(adauga)}. `}
            {scoate.length > 0 && `Ies: ${nume(scoate)}.`}
          </span>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={laInchidere}>
              Renunță
            </Button>
            <Button
              disabled={(adauga.length === 0 && scoate.length === 0) || schimba.isPending}
              onClick={() =>
                schimba.mutate(
                  { id: task.id, adauga, scoate },
                  {
                    onSuccess: () => {
                      toast.success(
                        alesi.length
                          ? `Pe task: ${nume(alesi)}`
                          : task.privat
                            ? 'Task-ul s-a întors la cine l-a dat'
                            : 'Task-ul s-a întors la „De preluat”',
                      )
                      laInchidere()
                    },
                  },
                )
              }
            >
              Salvează
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
